import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Avatar, Button, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Nfc as NfcIcon,
  People as PeopleIcon,
  CardGiftcard as RewardIcon,
  Add as AddIcon,
  Edit as EditIcon,
  PlayArrow as TriggerIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import corporateService, { CorporateEmployee, CorporateStats } from '../../services/corporate.service';
import EmployeeModal from './EmployeeModal';
import CorporateModal from './CorporateModal';

const BRAND = '#6233A0';

const EmpresaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = Number(id);
  const isValid = !!id && !isNaN(clientId) && clientId > 0;

  // ── todos os hooks sempre no topo, sem condicional ────────────────────────
  const [stats, setStats] = useState<CorporateStats | null>(null);
  const [employees, setEmployees] = useState<CorporateEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [distributing, setDistributing] = useState(false);
  const [empModalOpen, setEmpModalOpen] = useState(false);
  const [editClient, setEditClient] = useState(false);
  const [editEmployee, setEditEmployee] = useState<CorporateEmployee | null>(null);

  const load = async () => {
    if (!isValid) return;
    setLoading(true);
    setError(null);
    try {
      const [s, e] = await Promise.all([
        corporateService.getStats(clientId),
        corporateService.getEmployees(clientId),
      ]);
      setStats(s);
      setEmployees(e);
    } catch {
      setError('Erro ao carregar dados da empresa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isValid) {
      navigate('/corporate', { replace: true });
      return;
    }
    load();
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── guard de render após hooks ────────────────────────────────────────────
  if (!isValid) return null;

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  const handleTrigger = async () => {
    if (!window.confirm('Distribuir recompensas agora para todos os funcionários activos?')) return;
    setDistributing(true);
    try {
      const result = await corporateService.triggerDistribution(clientId);
      alert(`Distribuição concluída: ${result.result.completed} ok, ${result.result.failed} falhas, ${result.result.skipped} ignorados`);
      load();
    } catch (err: any) {
      alert('Erro: ' + (err?.response?.data?.error || err.message));
    } finally {
      setDistributing(false);
    }
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton onClick={() => navigate('/corporate')}><ArrowBack /></IconButton>
        <Avatar sx={{ bgcolor: BRAND }}><NfcIcon /></Avatar>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={700}>{stats?.empresa || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">Corporate NFC</Typography>
        </Box>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditClient(true)}>
          Editar
        </Button>
        <Button
          variant="contained"
          startIcon={distributing ? <CircularProgress size={16} color="inherit" /> : <TriggerIcon />}
          onClick={handleTrigger}
          disabled={distributing}
          sx={{ bgcolor: BRAND, '&:hover': { bgcolor: '#4e2880' } }}
        >
          Distribuir Agora
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Funcionários activos',      value: stats.funcionarios.ativos,                                           icon: <PeopleIcon />, color: BRAND },
            { label: 'Com bracelet NFC',           value: stats.funcionarios.com_bracelet,                                     icon: <NfcIcon />,    color: '#10B981' },
            { label: 'Budget mensal (ECV)',         value: stats.funcionarios.budget_mensal.toLocaleString('pt-CV'),            icon: <RewardIcon />, color: '#F59E0B' },
            { label: 'Distribuído este mês (ECV)', value: stats.distribuicoes.distribuido_mes_atual.toLocaleString('pt-CV'),   icon: <RewardIcon />, color: '#0D7F9F' },
          ].map(s => (
            <Grid item xs={6} md={3} key={s.label}>
              <Paper sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color, width: 32, height: 32 }}>{s.icon}</Avatar>
                </Box>
                <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Funcionários */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700}>Funcionários</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEmpModalOpen(true)}>
          Adicionar Funcionário
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><b>Nome</b></TableCell>
              <TableCell><b>Departamento</b></TableCell>
              <TableCell><b>Cargo</b></TableCell>
              <TableCell align="right"><b>Recompensa (ECV)</b></TableCell>
              <TableCell align="center"><b>Bracelet</b></TableCell>
              <TableCell align="right"><b>Saldo Bonus</b></TableCell>
              <TableCell align="center"><b>Estado</b></TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum funcionário registado
                </TableCell>
              </TableRow>
            ) : employees.map(emp => (
              <TableRow key={emp.id} hover>
                <TableCell>{emp.nome}</TableCell>
                <TableCell>{emp.departamento || '—'}</TableCell>
                <TableCell>{emp.cargo || '—'}</TableCell>
                <TableCell align="right">{Number(emp.reward_amount).toLocaleString('pt-CV')}</TableCell>
                <TableCell align="center">
                  {emp.nfc_uid ? (
                    <Chip label={emp.nfc_uid.substring(0, 8) + '…'} size="small" color="success" />
                  ) : (
                    <Chip label="Sem bracelet" size="small" color="default" />
                  )}
                </TableCell>
                <TableCell align="right">
                  {emp.saldo_bonus != null ? `${Number(emp.saldo_bonus).toLocaleString('pt-CV')} ECV` : '—'}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={emp.ativo ? 'Activo' : 'Inactivo'}
                    size="small"
                    color={emp.ativo ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => setEditEmployee(emp)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modals */}
      <EmployeeModal
        open={empModalOpen || !!editEmployee}
        onClose={() => { setEmpModalOpen(false); setEditEmployee(null); }}
        onSaved={() => { setEmpModalOpen(false); setEditEmployee(null); load(); }}
        clientId={clientId}
        initial={editEmployee || undefined}
      />

      <CorporateModal
        open={editClient}
        onClose={() => setEditClient(false)}
        onSaved={() => { setEditClient(false); load(); }}
      />
    </Box>
  );
};

export default EmpresaDetail;
